import { z } from "zod";

// Summary of all compute pools
export const computePoolSummaryItemSchema = z
  .object({
    compute_pool_name: z.string(),
    total_cost: z.number().or(z.string().transform(Number)),
    total_runs: z.number().optional().nullable().transform(val => (val ?? undefined) as number | undefined),
    owner: z.string().optional().nullable(),
    is_exclusive: z.boolean().optional().nullable(),
    application_name: z.string().optional().nullable(),
  })
  .passthrough();

export type ComputePoolSummaryItem = z.infer<typeof computePoolSummaryItemSchema>;

export const computePoolDailyCostItemSchema = z
  .object({
    date: z.string(),
    cost: z.number().or(z.string().transform(Number)).optional(),
    credits_used: z.number().or(z.string().transform(Number)).optional(),
  })
  .passthrough();

export type ComputePoolDailyCostItem = z.infer<typeof computePoolDailyCostItemSchema>;

export const computePoolMetadataItemSchema = z
  .object({
    name: z.string(),
    is_suspended: z.boolean().optional().nullable(),
    min_nodes: z.number().or(z.string().transform(Number)).optional().nullable(),
    max_nodes: z.number().or(z.string().transform(Number)).optional().nullable(),
    instance_family: z.string().optional().nullable(),
    auto_suspend_secs: z.number().or(z.string().transform(Number)).optional().nullable(),
    auto_resume: z.boolean().optional().nullable(),
    created: z.string().optional().nullable(),
    last_resumed: z.string().optional().nullable(),
    last_altered: z.string().optional().nullable(),
    deleted: z.string().optional().nullable(),
    owner: z.string().optional().nullable(),
    owner_role_type: z.string().optional().nullable(),
    is_exclusive: z.boolean().optional().nullable(),
    application_name: z.string().optional().nullable(),
    application_id: z.number().or(z.string().transform(Number)).optional().nullable(),
    comment: z.string().optional().nullable(),
    total_cost: z.number().or(z.string().transform(Number)).optional().nullable(),
  })
  .passthrough();

export type ComputePoolMetadataItem = z.infer<typeof computePoolMetadataItemSchema>;

export const computePoolOverviewSchema = z
  .object({
    compute_pool_name: z.string(),
    total_cost: z.number().or(z.string().transform(Number)),
    total_runs: z.number().or(z.string().transform(Number)).optional().nullable(),
    // New backend shape nests metadata
    metadata: computePoolMetadataItemSchema.optional(),
  })
  .passthrough();

export type ComputePoolOverview = z.infer<typeof computePoolOverviewSchema>;

export const computePoolEntrySchema = z
  .object({
    compute_pool_name: z.string().optional().nullable(),
    start_time: z.string(),
    end_time: z.string().optional().nullable(),
    credits_used: z.number().or(z.string().transform(Number)),
    application_name: z.string().optional().nullable(),
    application_id: z.union([z.string(), z.number()]).optional().nullable(),
    is_exclusive: z.boolean().optional().nullable(),
    instance_id: z.string().optional().nullable(),
  })
  .passthrough();

export type ComputePoolEntry = z.infer<typeof computePoolEntrySchema>;

export const computePoolsGraphDataSchema = z.array(
  z.object({
    date: z.string(),
    pool_spends: z.record(z.number().or(z.string().transform(Number))),
  })
);

export type ComputePoolsGraphDataItem = z.infer<typeof computePoolsGraphDataSchema>[number];

// Aggregated summary for all compute pools
export const computePoolsSummarySchema = z.object({
  total_spend: z.number().or(z.string().transform(Number)),
  average_daily_spend: z.number().or(z.string().transform(Number)),
  active_pools: z.number().or(z.string().transform(Number)),
  exclusive_pools: z.number().or(z.string().transform(Number)),
});

export type ComputePoolsSummary = z.infer<typeof computePoolsSummarySchema>;


